import { cn } from "@/lib/utils/cn";
import { type MascotEmotion, poseFor } from "@/lib/mascot/emotions";

export type MascotPose =
  | "idle"
  | "happy"
  | "wink"
  | "cheer"
  | "piggy"
  | "phone"
  | "money"
  | "reading"
  | "bag"
  | "sleepy"
  | "sadClipboard"
  | "sadHug"
  | "sadTear"
  | "peace"
  | "idea"
  | "task"
  | "winner"
  | "shield"
  | "surprise"
  // ── Nowe pozy (2026-05-10) ──
  | "celebrateConfetti" // celebrate variant — konfetti dookoła
  | "focused2"          // focused variant — z miotełką/gąbką
  | "outdoor"           // emocja `outdoor` — biegnie/skacze na zewnątrz
  | "proud2"            // proud variant — medal/trofeum
  | "reading2"          // curious variant — czyta listę zadań
  | "saving2"           // saving variant — wrzuca monetę do skarbonki
  | "sleepyYawn"        // sleepy variant — ziewa
  | "thinking"          // emocja `thinking` — palec przy ustach
  | "waiting";          // emocja `waiting` — siedzi cierpliwie

const POSE_FILES: Record<MascotPose, string> = {
  idle: "/mascot/idle.png",
  happy: "/mascot/wink-thumb.png",
  wink: "/mascot/wink-thumb.png",
  cheer: "/mascot/jump-cheer.png",
  piggy: "/mascot/cheer-piggy.png",
  phone: "/mascot/phone.png",
  money: "/mascot/money-fan.png",
  reading: "/mascot/reading.png",
  bag: "/mascot/bag-zl.png",
  sleepy: "/mascot/sad-empty.png",
  sadClipboard: "/mascot/sad-clipboard.png",
  sadHug: "/mascot/hug-piggy.png",
  sadTear: "/mascot/sad-tear.png",
  peace: "/mascot/peace.png",
  idea: "/mascot/idea.png",
  task: "/mascot/task.png",
  winner: "/mascot/winner.png",
  shield: "/mascot/shield.png",
  surprise: "/mascot/surprise.png",
  celebrateConfetti: "/mascot/celebrate-confetti.png",
  focused2: "/mascot/focused.png",
  outdoor: "/mascot/outdoor.png",
  proud2: "/mascot/proud-2.png",
  reading2: "/mascot/reading-2.png",
  saving2: "/mascot/saving-2.png",
  sleepyYawn: "/mascot/sleepy-yawn.png",
  thinking: "/mascot/thinking.png",
  waiting: "/mascot/waiting.png",
};

type Props = {
  /**
   * Bezpośrednia poza (warstwa 1, low-level). Backward-compat — istniejące
   * call-site'y nadal działają. Dla nowych użyć preferuj `emotion`.
   */
  pose?: MascotPose;
  /**
   * Semantyczna emocja (warstwa 2). Gdy podana, ignoruje `pose` i wybiera
   * pozę z `EMOTION_POSES[emotion]`. Pozwala spójnie zmieniać look całej apki
   * przez edycję jednej mapy.
   */
  emotion?: MascotEmotion;
  /**
   * Wariant pozy w obrębie emocji (deterministic seed). 0 = pierwsza,
   * 1 = druga, itd. Modulo długość listy.
   */
  variant?: number;
  size?: number;
  flip?: boolean;
  shadow?: boolean;
  className?: string;
  alt?: string;
};

// Bumpować przy każdej zmianie pliku maskotki — Firebase Hosting trzyma
// PNG na 1 rok (immutable), więc PWA nie pobierze nowej grafiki bez nowego URL.
const MASCOT_CACHE_VERSION = "174";

export function Mascot({
  pose,
  emotion,
  variant = 0,
  size = 120,
  flip = false,
  shadow = true,
  className,
  alt = "Sianko",
}: Props) {
  const resolvedPose: MascotPose = emotion
    ? poseFor(emotion, variant)
    : (pose ?? "idle");
  return (
    <img
      src={`${POSE_FILES[resolvedPose]}?v=${MASCOT_CACHE_VERSION}`}
      alt={alt}
      className={cn("block max-w-none", className)}
      style={{
        height: size,
        width: "auto",
        transform: flip ? "scaleX(-1)" : undefined,
        filter: shadow ? "drop-shadow(0 6px 10px rgba(30,41,59,0.18))" : undefined,
      }}
    />
  );
}
